import { useState, memo, useEffect, useContext, useRef } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Slide,
  Popper,
  InputBase,
  PopperProps,
  Autocomplete,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';

import Iconify from '../../iconify';
import IconButtonAnimate from '../../animate/IconButtonAnimate';
import DataContext from '../../../context/DataProvider/DataContext';
import { PagePathname } from '../../../pages';
import AgentStatus from '../../../types/enums';
import { MARKERS } from '../../../features/usage-tracking/usageKeys';
import type { UsageSearchName } from '../../../features/usage-tracking/usageKeys';
import { usageTracker } from '../../../features/usage-tracking/services/tracker';
import { describeSearch } from '../../../features/usage-tracking/services/searchUsage';
import { filterSearchbarOptions, searchbarOptionLabel, searchbarRank } from './searchbarFilter';
import { Agent } from '../../../types/Agent.types';

// ----------------------------------------------------------------------

/** Name des Suchereignisses aus dem Schlüsselverzeichnis (RITA-1504, Entwurf D). */
const SUCHE_NAME: UsageSearchName = 'kopf.suche';

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const StyledSearchbar = styled('div')(({ theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: APPBAR_MOBILE,
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

const StyledPopper = styled((props: PopperProps) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Popper {...props} />
))(({ theme }) => ({
  left: `8px !important`,
  top: `${APPBAR_MOBILE + 8}px !important`,
  width: 'calc(100% - 16px) !important',
  transform: 'none !important',
  [theme.breakpoints.up('md')]: {
    top: `${APPBAR_DESKTOP + 8}px !important`,
  },
  '& .MuiAutocomplete-paper': {
    padding: theme.spacing(1, 0),
  },
  '& .MuiListSubheader-root': {
    '&.MuiAutocomplete-groupLabel': {
      ...bgBlur({ color: theme.palette.background.neutral }),
      ...theme.typography.overline,
      top: 0,
      margin: 0,
      lineHeight: '48px',
      borderRadius: theme.shape.borderRadius,
    },
  },
  '& .MuiAutocomplete-listbox': {
    '& .MuiAutocomplete-option': {
      padding: theme.spacing(0.5, 2),
      margin: 0,
      display: 'block',
      border: `dashed 1px transparent`,
      borderBottomColor: theme.palette.divider,
      '&:last-of-type': {
        borderBottomColor: 'transparent',
      },
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
      },
    },
  },
}));

// ----------------------------------------------------------------------

function Searchbar() {
  const { agents } = useContext(DataContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<number>();
  // Nur die LÄNGE der Eingabe wird gehalten; der Suchtext selbst bleibt im Autocomplete und
  // erreicht die Erfassung nie (RITA-1504, Entwurf D).
  const [queryLength, setQueryLength] = useState(0);
  // Zuletzt angezeigte Trefferliste — Grundlage für Trefferzahl und Rang. Als Ref, weil sie beim
  // Filtern (also während der Darstellung) entsteht und die Darstellung nicht beeinflusst.
  const trefferRef = useRef<Agent[]>([]);
  // Riegel: je Öffnung der Suche genau ein Ereignis — Auswahl ODER Schließen ohne Auswahl.
  const gemeldetRef = useRef(false);

  /**
   * Meldet die Suche einmalig. `rank` gesetzt = ein Treffer wurde gewählt; ohne Auswahl und ohne
   * Eingabe wurde nicht gesucht, dann bleibt es still.
   */
  const meldeSuche = (rank?: number) => {
    if (gemeldetRef.current) return;
    if (rank === undefined && queryLength === 0) return;
    gemeldetRef.current = true;
    try {
      usageTracker.record({
        type: 'search',
        name: SUCHE_NAME,
        props: describeSearch({ queryLength, results: trefferRef.current.length, rank }),
      });
    } catch {
      // Erfassung darf die Bedienung nicht stören.
    }
  };

  const handleOpen = () => {
    gemeldetRef.current = false;
    trefferRef.current = [];
    setQueryLength(0);
    setOpen(true);
  };

  const handleClose = () => {
    meldeSuche();
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-line
  }, [pathname]);

  const handleClick = (agent_id: number | undefined) => {
    if (!agent_id) return;
    const path = PagePathname.beraterBearbeiten.split(':')[0] + agent_id;
    if (path.includes('http')) {
      window.open(path);
    } else {
      navigate(path);
    }
    handleClose();
  };

  /** Auswahl per Maus: erst melden (der Rang steht nur in der angezeigten Liste), dann öffnen. */
  const handleSelect = (option: Agent) => {
    meldeSuche(searchbarRank(trefferRef.current, option));
    handleClick(option.agent_id);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleClick(searchQuery);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!open && (
          <IconButtonAnimate onClick={handleOpen} data-usage={MARKERS.kopfSucheOeffnen}>
            <Iconify icon="eva:search-fill" />
          </IconButtonAnimate>
        )}

        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <StyledSearchbar>
            <Autocomplete
              sx={{ width: 1, height: 1 }}
              autoHighlight
              disablePortal
              disableClearable
              popupIcon={null}
              PopperComponent={StyledPopper}
              onChange={(event, value) => {
                // Auswahl per Tastatur (autoHighlight + Enter); der Mausklick läuft über
                // handleSelect in renderOption.
                meldeSuche(searchbarRank(trefferRef.current, value));
                setSearchQuery(value?.agent_id);
              }}
              onInputChange={(event, value, reason) => {
                // Nur die Länge. 'reset' ist die Rückmeldung des Autocomplete nach einer Auswahl
                // und keine Eingabe des Bedienenden.
                if (reason === 'input') setQueryLength(value.length);
              }}
              noOptionsText="keine Einträge gefunden"
              options={agents}
              filterOptions={(options, state) => {
                // Der eine Filter der Kopfsuche: Er liefert die angezeigte Liste und legt sie für
                // Trefferzahl und Rang ab (kein Melden während der Darstellung).
                const treffer = filterSearchbarOptions(options, state);
                trefferRef.current = treffer;
                return treffer;
              }}
              getOptionLabel={searchbarOptionLabel}
              renderInput={(params) => (
                <InputBase
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  fullWidth
                  autoFocus
                  placeholder="Suchen..."
                  onKeyUp={handleKeyUp}
                  startAdornment={
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  }
                  sx={{ height: 1, typography: 'h6' }}
                />
              )}
              renderOption={(props, option, { inputValue }) => {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const { name, surname, agent_id: agentId, status } = option;

                const partsTitle = parse(
                  `${agentId} ${name} ${surname}`,
                  match(`${agentId} ${name} ${surname}`, inputValue),
                );

                return (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <Box component="li" {...props} onClick={() => handleSelect(option)}>
                    {partsTitle.map((part, index) => {
                      let color = 'text.primary';
                      if (status.id === AgentStatus.dismissed) {
                        color = 'red';
                      } else if (part.highlight) {
                        color = 'primary.main';
                      }
                      return (
                        <Box
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          component="span"
                          sx={{
                            typography: 'subtitle2',
                            textTransform: 'capitalize',
                            color,
                          }}
                        >
                          {part.text}
                        </Box>
                      );
                    })}
                  </Box>
                );
              }}
            />
          </StyledSearchbar>
        </Slide>
      </div>
    </ClickAwayListener>
  );
}

export default memo(Searchbar);
